// Cobro del hotel con pagos MIXTOS, igual que la caja del POS (943/944):
// las formas de pago son las del catálogo de la empresa; sobre la forma de
// tarjeta configurada en la pasarela aparece «Cobrar con tarjeta (enlace)» y
// sobre la de De Una «Cobrar con De Una (QR)» — con la pasarela PROPIA del
// hotel (945). El QR se muestra en la misma línea (sin
// ventanas flotantes). Tarjeta por datáfono: adquirente, marca, lote, voucher,
// autorización, últimos 4, terminal y diferido, recomendados (no bloquean).
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { QRCodeSVG } from 'qrcode.react';
import { CheckCircle2, Copy, Loader2, Plus, QrCode, Trash2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { PASARELA_ACTIVO, pasarelaAnular, pasarelaCrearCobro, pasarelaEstado, type PasarelaCobro, type PasarelaModos } from '@/features/pos/shared';
import { Campo, fieldCls, money, hotelRpc, QK } from './shared';

export interface FormaCatalogo { id: string; codigo: string; nombre: string; tipo: string; requiere_referencia: boolean }
interface CobroFormas { formas: FormaCatalogo[]; pasarela: PasarelaModos; hereda_pos: boolean }

export interface LineaPago {
  key: string; forma_pago_id: string; monto: number; ventana_numero: number; referencia: string;
  adquirente: string; marca: string; lote: string; voucher: string; autorizacion: string; ultimos4: string; terminal: string; diferido: string;
  medio?: 'tarjeta' | 'deuna' | null;          // si la línea se cobra por la pasarela
  cobro?: PasarelaCobro | null;
}
const r2 = (n: number) => Math.round(n * 100) / 100;
export const nuevaLinea = (monto: number, ventana = 1, formaPagoId = ''): LineaPago => ({
  key: Math.random().toString(36).slice(2), forma_pago_id: formaPagoId, monto: r2(Math.max(0, monto)), ventana_numero: ventana, referencia: '',
  adquirente: '', marca: '', lote: '', voucher: '', autorizacion: '', ultimos4: '', terminal: '', diferido: '', medio: null, cobro: null,
});
const cobroOk = (l: LineaPago) => !!l.cobro && (l.cobro.estado === 'autorizado' || l.cobro.estado === 'pagado');

export function useCobroFormas() {
  return useQuery<CobroFormas>({ queryKey: [QK, 'cobro-formas'], queryFn: () => hotelRpc<CobroFormas>('cobro_formas', {}), staleTime: 0 });
}

/** Contrato de las RPC (deposito_cobrar / checkout). */
export const pagosParaRpc = (lineas: LineaPago[]) => lineas.filter((l) => l.monto > 0).map((l) => ({
  forma_pago_id: l.forma_pago_id || null, monto: r2(l.monto), ventana_numero: l.ventana_numero,
  referencia: l.referencia.trim() || null, cobro_id: l.cobro && cobroOk(l) ? l.cobro.id : null,
  adquirente: l.adquirente || null, marca: l.marca || null, lote: l.lote.trim() || null, voucher: l.voucher.trim() || null,
  autorizacion: l.autorizacion.trim() || null, ultimos4: l.ultimos4.trim() || null, terminal: l.terminal.trim() || null,
  diferido_meses: l.diferido ? Number(l.diferido) || null : null,
}));

export function validarPagos(lineas: LineaPago[], total: number, formas: FormaCatalogo[] = [], modos?: PasarelaModos | null):
  { ok: boolean; motivo: string | null; recibido: number; vuelto: number; falta: number } {
  const tipo = (l: LineaPago) => formas.find((f) => f.id === l.forma_pago_id)?.tipo;
  const recibido = r2(lineas.reduce((a, l) => a + Number(l.monto || 0), 0));
  const noEfectivo = r2(lineas.filter((l) => tipo(l) !== 'efectivo').reduce((a, l) => a + Number(l.monto || 0), 0));
  const falta = r2(Math.max(0, total - recibido));
  const vuelto = r2(Math.max(0, recibido - total));
  let motivo: string | null = null;
  if (lineas.some((l) => !l.forma_pago_id)) motivo = 'Elija la forma de pago';
  else if (lineas.some((l) => !(l.monto > 0))) motivo = 'Hay pagos sin monto';
  else if (lineas.some((l) => modos?.deuna && l.forma_pago_id === modos.forma_pago_deuna_id && !cobroOk(l))) motivo = 'De Una se cobra con el QR: falta que el huésped pague';
  else if (lineas.some((l) => l.medio && !cobroOk(l))) motivo = 'Hay un cobro por enlace que aún no está pagado';
  else if (lineas.some((l) => { const f = formas.find((x) => x.id === l.forma_pago_id); return f?.requiere_referencia && f.tipo !== 'tarjeta' && f.tipo !== 'efectivo' && !l.cobro && !l.referencia.trim(); }))
    motivo = 'Falta la referencia / número de comprobante';
  else if (total > 0 && noEfectivo > total + 0.004) motivo = 'Con tarjeta, De Una o transferencia no se puede cobrar más del total';
  return { ok: motivo == null, motivo, recibido, vuelto, falta };
}

export function CobroHotel({ total, lineas, onChange, partnerId, ventanas, exigirTotal = true }: {
  total: number; lineas: LineaPago[]; onChange: (l: LineaPago[]) => void; partnerId: string | null;
  ventanas?: { numero: number; nombre: string; saldo: number }[]; exigirTotal?: boolean;
}) {
  const { data } = useCobroFormas();
  const formas = useMemo(() => data?.formas ?? [], [data]);
  const modos = data?.pasarela;
  const efectivo = formas.find((f) => f.tipo === 'efectivo')?.id ?? formas[0]?.id ?? '';
  // Las líneas iniciales nacen sin forma: se les pone efectivo apenas carga el catálogo
  useEffect(() => {
    if (efectivo && lineas.some((l) => !l.forma_pago_id)) onChange(lineas.map((l) => (l.forma_pago_id ? l : { ...l, forma_pago_id: efectivo })));
  }, [efectivo, lineas, onChange]);
  const v = validarPagos(lineas, total, formas, modos);
  const set = (key: string, patch: Partial<LineaPago>) => onChange(lineas.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  return (
    <div className="space-y-2">
      {lineas.map((l) => (
        <LineaCobro key={l.key} l={l} formas={formas} modos={modos} ventanas={ventanas} partnerId={partnerId}
          onChange={(p) => set(l.key, p)} onQuitar={() => onChange(lineas.filter((x) => x.key !== l.key))} />
      ))}
      <Button type="button" size="sm" variant="outline" onClick={() => onChange([...lineas, nuevaLinea(v.falta, ventanas?.[0]?.numero ?? 1, efectivo)])}>
        <Plus className="mr-1 h-3.5 w-3.5" /> Pago
      </Button>
      {modos && (
        <p className="text-[11px] text-muted-foreground">
          Pasarela: {modos.enlace || modos.deuna
            ? <>PagoMedios {modos.ambiente === 'sandbox' ? '(pruebas)' : ''} · {[modos.enlace && 'tarjeta por enlace', modos.deuna && 'De Una'].filter(Boolean).join(' y ')}</>
            : <>{modos.mensaje ?? 'sin pasarela'} — configúrela en Hotel → Configuración → Pasarela de cobro</>}
        </p>
      )}
      <div className="grid grid-cols-2 gap-2 rounded-md bg-muted/40 p-2 text-sm md:grid-cols-4">
        <div><div className="text-[11px] text-muted-foreground">A cobrar</div><div className="font-semibold tabular-nums">{money(total)}</div></div>
        <div><div className="text-[11px] text-muted-foreground">Recibido</div><div className="font-semibold tabular-nums">{money(v.recibido)}</div></div>
        <div><div className="text-[11px] text-muted-foreground">{exigirTotal ? 'Falta' : 'Pendiente'}</div><div className={`font-semibold tabular-nums ${v.falta > 0 && exigirTotal ? 'text-red-600' : ''}`}>{money(v.falta)}</div></div>
        <div><div className="text-[11px] text-muted-foreground">Vuelto (efectivo)</div><div className="font-semibold tabular-nums text-amber-700 dark:text-amber-400">{money(v.vuelto)}</div></div>
      </div>
      {v.motivo && lineas.length > 0 && <p className="text-xs text-red-600">{v.motivo}</p>}
    </div>
  );
}

/** Valida con el catálogo cargado (para habilitar el botón de confirmar). */
export function useValidarPagos(lineas: LineaPago[], total: number) {
  const { data } = useCobroFormas();
  return validarPagos(lineas, total, data?.formas ?? [], data?.pasarela);
}

function LineaCobro({ l, formas, modos, ventanas, partnerId, onChange, onQuitar }: {
  l: LineaPago; formas: FormaCatalogo[]; modos?: PasarelaModos; ventanas?: { numero: number; nombre: string; saldo: number }[]; partnerId: string | null;
  onChange: (p: Partial<LineaPago>) => void; onQuitar: () => void;
}) {
  const forma = formas.find((f) => f.id === l.forma_pago_id);
  const esEnlace = !!modos?.enlace && modos.forma_pago_id === l.forma_pago_id;
  const esDeuna = !!modos?.deuna && modos.forma_pago_deuna_id === l.forma_pago_id;
  const esTarjeta = forma?.tipo === 'tarjeta';
  const pagado = cobroOk(l);
  const vivo = !!l.cobro && PASARELA_ACTIVO.includes(l.cobro.estado);
  const [creando, setCreando] = useState(false);

  useEffect(() => {
    if (!vivo || !l.cobro) return;
    const id = l.cobro.id;
    const t = setInterval(() => {
      pasarelaEstado(id).then((c) => {
        if (c.estado !== l.cobro?.estado || c.url !== l.cobro?.url) {
          const ok = c.estado === 'autorizado' || c.estado === 'pagado';
          onChange({ cobro: c, ...(ok ? { autorizacion: c.autorizacion ?? '', lote: c.lote ?? '', ultimos4: c.ultimos4 ?? '', marca: c.marca ?? '',
            terminal: c.tid ?? '', adquirente: c.adquirente ?? c.proveedor ?? '', referencia: c.referencia_pago ?? c.orden } : {}) });
          if (ok) toast.success(`Pago ${c.orden} autorizado`);
          if (['rechazado', 'expirado', 'error', 'anulado'].includes(c.estado)) toast.error(`El cobro ${c.orden} quedó ${c.estado}`);
        }
      }).catch(() => undefined);
    }, 2500);
    return () => clearInterval(t);
  }, [vivo, l.cobro, onChange]);

  const generar = async (medio: 'tarjeta' | 'deuna') => {
    if (!(l.monto > 0)) { toast.error('Indique el monto'); return; }
    setCreando(true);
    try { onChange({ medio, cobro: await pasarelaCrearCobro(l.monto, 'hotel', null, partnerId, medio) }); }
    catch (e) { toast.error(getErrorMessage(e)); } finally { setCreando(false); }
  };
  const cancelarCobro = async () => {
    if (l.cobro && vivo) { try { await pasarelaAnular(l.cobro.id); } catch { /* pudo vencer */ } }
    onChange({ cobro: null, medio: null });
  };

  return (
    <div className={`space-y-2 rounded-md border p-2 ${pagado ? 'border-emerald-400 bg-emerald-50/50 dark:bg-emerald-900/10' : ''}`}>
      <div className="grid grid-cols-[1fr_7rem_2rem] items-end gap-2 md:grid-cols-[1fr_7rem_10rem_2rem]">
        <Campo label="Forma de pago">
          <select className={fieldCls} value={l.forma_pago_id} disabled={!!l.cobro} onChange={(e) => onChange({ forma_pago_id: e.target.value, referencia: '' })}>
            {!l.forma_pago_id && <option value="">—</option>}
            {formas.map((f) => <option key={f.id} value={f.id}>{f.nombre}</option>)}
          </select>
        </Campo>
        <Campo label="Monto"><input type="number" min={0} step="0.01" className={`${fieldCls} text-right`} value={l.monto} disabled={!!l.cobro}
          onFocus={(e) => e.currentTarget.select()} onChange={(e) => onChange({ monto: Number(e.target.value) || 0 })} /></Campo>
        {ventanas && ventanas.length > 1 ? (
          <Campo label="Cuenta" className="hidden md:block">
            <select className={fieldCls} value={l.ventana_numero} onChange={(e) => onChange({ ventana_numero: Number(e.target.value) })}>
              {ventanas.map((w) => <option key={w.numero} value={w.numero}>{w.numero} · {w.nombre}</option>)}
            </select>
          </Campo>
        ) : <div className="hidden md:block" />}
        <button type="button" className="mb-1 h-10 rounded text-red-500 hover:bg-muted disabled:opacity-40" disabled={pagado}
          title={pagado ? 'Ya cobrado: no se puede quitar' : 'Quitar'} onClick={() => { void cancelarCobro(); onQuitar(); }}><Trash2 className="mx-auto h-4 w-4" /></button>
      </div>

      {/* Pasarela: igual que la caja — botón sobre la forma configurada */}
      {(esEnlace || esDeuna) && !l.cobro && (
        <div className="flex flex-wrap items-center gap-2">
          {esEnlace && <Button size="sm" variant="outline" isLoading={creando} disabled={!(l.monto > 0)} onClick={() => void generar('tarjeta')}><QrCode className="mr-1 h-4 w-4" /> Cobrar con tarjeta (enlace)</Button>}
          {esDeuna && <Button size="sm" variant="outline" isLoading={creando} disabled={!(l.monto > 0)} onClick={() => void generar('deuna')}><QrCode className="mr-1 h-4 w-4" /> Cobrar con De Una (QR)</Button>}
          {esEnlace && <span className="text-[11px] text-muted-foreground">o registre abajo los datos del datáfono</span>}
        </div>
      )}
      {l.cobro && vivo && (
        <div className="flex flex-wrap items-start gap-3">
          {l.cobro.url ? <div className="rounded bg-white p-2"><QRCodeSVG value={l.cobro.url} size={140} /></div>
            : <div className="flex h-[156px] w-[156px] items-center justify-center rounded border"><Loader2 className="h-6 w-6 animate-spin" /></div>}
          <div className="space-y-1 text-xs">
            <div className="font-medium">{l.cobro.orden} · {money(Number(l.cobro.monto))} · {l.medio === 'deuna' ? 'De Una' : 'tarjeta'}</div>
            <div className="flex items-center gap-1 text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin" /> El huésped escanea el QR y paga en su celular…</div>
            {l.cobro.url && (
              <button type="button" className="flex items-center gap-1 text-primary underline" onClick={() => { void navigator.clipboard?.writeText(l.cobro?.url ?? ''); toast.success('Enlace copiado: puede enviarlo por WhatsApp o correo'); }}>
                <Copy className="h-3 w-3" /> Copiar enlace
              </button>
            )}
            <button type="button" className="text-red-600 underline" onClick={() => void cancelarCobro()}>Cancelar este cobro</button>
          </div>
        </div>
      )}
      {l.cobro && pagado && (
        <div className="flex items-center gap-2 text-sm text-emerald-700 dark:text-emerald-400">
          <CheckCircle2 className="h-5 w-5" /> ✓ Pagado · {l.cobro.orden}{l.cobro.referencia_pago ? ` · ref ${l.cobro.referencia_pago}` : ''}
        </div>
      )}
      {l.cobro && !vivo && !pagado && (
        <div className="flex items-center gap-2 text-sm text-red-600">
          <XCircle className="h-5 w-5" /> {l.cobro.orden} {l.cobro.estado}{l.cobro.error ? `: ${l.cobro.error}` : ''}
          <Button type="button" size="sm" variant="outline" onClick={() => onChange({ cobro: null, medio: null })}>Intentar de nuevo</Button>
        </div>
      )}

      {forma?.requiere_referencia && !esTarjeta && !esDeuna && !l.cobro && (
        <input className={`${fieldCls} h-9`} placeholder="Referencia / N° de comprobante" value={l.referencia} onChange={(e) => onChange({ referencia: e.target.value })} />
      )}

      {esTarjeta && !esDeuna && (
        <div className="space-y-1.5 rounded-md border border-dashed bg-muted/20 p-2">
          <p className="text-xs font-medium text-muted-foreground">
            Datos de la tarjeta (conciliación con el banco){pagado && <span className="ml-2 font-normal text-emerald-700 dark:text-emerald-400">· los llenó la pasarela</span>}
          </p>
          {pagado ? (
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              {([['Adquirente', l.adquirente], ['Marca', l.marca], ['Autorización', l.autorizacion], ['Lote', l.lote], ['Terminal', l.terminal], ['Últimos 4', l.ultimos4 ? `····${l.ultimos4}` : '']] as [string, string][]).map(([k, val]) => (
                <div key={k} className="flex justify-between gap-2"><span className="text-muted-foreground">{k}</span><span className="font-mono">{val || '—'}</span></div>
              ))}
            </div>
          ) : !l.cobro && (
            <>
              <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
                <Campo label="Adquirente">
                  <select className={`${fieldCls} h-9`} value={l.adquirente} onChange={(e) => onChange({ adquirente: e.target.value })}>
                    <option value="">—</option><option value="datafast">Datafast</option><option value="medianet">Medianet</option><option value="otro">Otro</option>
                  </select>
                </Campo>
                <Campo label="Marca">
                  <select className={`${fieldCls} h-9`} value={l.marca} onChange={(e) => onChange({ marca: e.target.value })}>
                    <option value="">—</option><option value="Visa">Visa</option><option value="Mastercard">Mastercard</option><option value="Diners">Diners</option>
                    <option value="American Express">American Express</option><option value="Otra">Otra</option>
                  </select>
                </Campo>
                <Campo label="N.º de lote *"><input className={`${fieldCls} h-9`} value={l.lote} onChange={(e) => onChange({ lote: e.target.value })} /></Campo>
                <Campo label="N.º de voucher / recibo *"><input className={`${fieldCls} h-9`} value={l.voucher} onChange={(e) => onChange({ voucher: e.target.value })} /></Campo>
                <Campo label="Código de autorización *"><input className={`${fieldCls} h-9`} value={l.autorizacion} onChange={(e) => onChange({ autorizacion: e.target.value })} /></Campo>
                <Campo label="Últimos 4 dígitos"><input className={`${fieldCls} h-9`} inputMode="numeric" maxLength={4} value={l.ultimos4} onChange={(e) => onChange({ ultimos4: e.target.value.replace(/\D/g, '').slice(0, 4) })} /></Campo>
                <Campo label="N.º de terminal"><input className={`${fieldCls} h-9`} value={l.terminal} onChange={(e) => onChange({ terminal: e.target.value })} /></Campo>
                <Campo label="Diferido (meses)"><input type="number" min={0} step={1} className={`${fieldCls} h-9`} value={l.diferido} onChange={(e) => onChange({ diferido: e.target.value })} /></Campo>
              </div>
              <p className="text-[11px] text-muted-foreground">* recomendados para cruzar con el archivo de Datafast/Medianet — no bloquean el cobro.</p>
            </>
          )}
        </div>
      )}
    </div>
  );
}
